//latest svn code
import React, { useState, useEffect, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import CaptchaInput from './CaptchaInput';
 
const CustomForm = ({
  title,
  onSubmit,
  navigateTo,
  formFields = { hawbNo: 'HAWB No', courierCompany: 'Courier Company (Portwise)' },
}) => {
  const [formData, setFormData] = useState({
    hawbNo: '',
    courierCompany: '',
  });
 
  const [inputValue, setInputValue] = useState('');
  const [filteredCouriers, setFilteredCouriers] = useState([]);
  const [noResultsMessage, setNoResultsMessage] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaText, setCaptchaText] = useState('');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [courierNames, setCourierNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hawbError, setHawbError] = useState('');
  const [hasServerError, setHasServerError] = useState(false);
  const [serverMessage, setServerMessage] = useState('');
  const [backendErrors, setBackendErrors] = useState([]);
 
  const navigate = useNavigate();
  const hawbRef = useRef(null);
  const courierRef = useRef(null);
  const captchaRef = useRef(null);
 
  const handleCaptchaTextChange = (newCaptcha) => {
    setCaptchaText(newCaptcha);
  };
 
  useEffect(() => {
    setLoading(true);
    fetch('/eicimobility/couriercompanies', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          if (response.status === 500) {
            setServerMessage('Oops, something went wrong. Please try again.');
            setHasServerError(true);
          }
          throw new Error('Failed to fetch data');
        }
        return response.json();
      })
      .then((data) => {
        setCourierNames(data);
        setLoading(false);
      })
      .catch((error) => {
        setError(error.message);
        setLoading(false);
      });
  }, []);
 
  useEffect(() => {
    if (inputValue) {
      const filtered = courierNames.filter((courier) =>
        courier.toLowerCase().startsWith(inputValue.toLowerCase())
      );
      setFilteredCouriers(filtered);
      setNoResultsMessage(filtered.length === 0 ? `No result found for "${inputValue}"` : '');
    } else {
      setFilteredCouriers([]);
      setNoResultsMessage('');
    }
  }, [inputValue, courierNames]);
 
  useEffect(() => {
    hawbRef.current?.focus();
  }, []);
 
  const handleChange = (e) => {
    const { name, value } = e.target;
 
    if (name === 'hawbNo') {
      if (value.length > 40) return;
 
      const hawbNoRegex = /^[a-zA-Z0-9\- ]*$/;
      if (!hawbNoRegex.test(value)) {
        setHawbError('Only alphanumeric characters, dashes, and spaces are allowed.');
      } else {
        setHawbError('');
      }
    }
 
    if (name === 'courierCompany') {
      if (value.length > 59) return;
    }
 
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
 
  const handleCourierNameChange = (e) => {
    const value = e.target.value;
    if (value.length > 59) return;
    handleChange(e);
    setInputValue(value);
    setIsDropdownVisible(value !== '');
  };
 
  const handleCourierSelect = (courier) => {
    setFormData((prev) => ({ ...prev, courierCompany: courier }));
    setInputValue(courier);
    setIsDropdownVisible(false);
    setFilteredCouriers([]);
    setNoResultsMessage('');
  };
 
  const handleCaptchaChange = (e) => {
    const value = e.target.value;
    if (value.length <= 7) {
      setCaptchaInput(value);
    }
  };
 
  const handleReset = () => {
    setFormData({ hawbNo: '', courierCompany: '' });
    setInputValue('');
    setFilteredCouriers([]);
    setCaptchaInput('');
    setHawbError('');
    setHasServerError(false);
    setBackendErrors([]);
    hawbRef.current?.focus();
  };
 
  const handleSubmit = async (e) => {
    e.preventDefault();
    setHasServerError(false);
    setBackendErrors([]);
 
    if (!formData.hawbNo) {
      alert('Please enter HAWB No.');
      hawbRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      hawbRef.current?.focus();
      return;
    }
 
    if (!formData.courierCompany) {
      alert('Please enter Courier Company.');
      courierRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      courierRef.current?.focus();
      return;
    }
 
    if (!courierNames.includes(formData.courierCompany)) {
      alert('Please select a valid courier name.');
      courierRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      courierRef.current?.focus();
      return;
    }
 
    const hawbNoRegex = /^[a-zA-Z0-9\- ]+$/;
    if (!hawbNoRegex.test(formData.hawbNo)) {
      alert('Invalid HAWB Number!...');
      hawbRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      hawbRef.current?.focus();
      return;
    }
 
    if (!captchaInput) {
      alert('Please enter captcha.');
      captchaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      captchaRef.current?.focus();
      return;
    }
 
    if (captchaInput !== captchaText) {
      alert('Incorrect captcha. Please try again.');
      captchaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      captchaRef.current?.focus();
      return;
    }
 
    try {
      const result = await onSubmit(formData);
      if (result?.status === 500) {
        setServerMessage(result?.message || 'Oops, something went wrong. Please try again.');
        setHasServerError(true);
        return;
      }
 
      if (result?.status === 'FAILURE') {
        if (result?.errorList?.length > 0) {
          setBackendErrors(result.errorList);
          setServerMessage('');
          setHasServerError(true);
          return;
        }
        setServerMessage(result?.errorMessage || 'Oops, something went wrong. Please try again.'); 
        setHasServerError(true);
        return;
      }
 
      if (result.status === 'SUCCESS') {
        navigate(navigateTo, { state: result });
      } else {
        navigate(navigateTo, {
          state: {
            ...result,
            status: 'FAILURE',
          },
        });
      }
    } catch (error) {
      setHasServerError(true);
      navigate(navigateTo, {
        state: {
          status: 'FAILURE',
          errorMessage: error.message || 'Unexpected error occurred.',
        },
      });
    }
  };
 
  return (
    <Container className="mt-4 import-form-carousel">
    {hasServerError ? (
    <>
      {serverMessage && (
        <div className="alert alert-danger mt-3">{serverMessage}</div>
      )}
  
      {backendErrors.length > 0 && (
        <div className="alert alert-danger mt-3">
          {backendErrors.map((err, i) => (
            <div key={i}>{err}</div>
          ))}
        </div>
      )}

       <div className="mt-3 text-end">
       <button type="button" className="btn btn-danger submit-btn return-btn" onClick={() => {setHasServerError(false);
          setBackendErrors([]);
          setServerMessage('');
          setFormData({ hawbNo: '', courierCompany: '' });
          setInputValue('');
          setCaptchaInput('');
          setHawbError('');}}
        >RETURN
        </button>
    </div>
    </>
    ) : (
      <form onSubmit={handleSubmit} className="import-form">
        <h4 className="mb-4 text-center p-2 form-header">{title}</h4>
        <div className="mb-3 form-body">
          <div className="row">
            <div className="col-md-4">
              <label className="form-label">{formFields.hawbNo}</label>
            </div>
            <div className="col-md-8">
              <input
                ref={hawbRef}
                type="text"
                name="hawbNo"
                className={`form-control ${hawbError ? 'is-invalid' : ''}`}
                placeholder={formFields.hawbNo}
                value={formData.hawbNo}
                onChange={handleChange}
              />
              {hawbError && <div className="invalid-feedback">{hawbError}</div>}
            </div>
          </div>
        </div>
 
        <div className="mb-3 form-body">
          <div className="row">
            <div className="col-md-4">
              <label className="form-label">{formFields.courierCompany}</label>
            </div>
            <div className="col-md-8">
              <input
                ref={courierRef}
                type="text"
                name="courierCompany"
                className="form-control"
                placeholder={formFields.courierCompany}
                value={inputValue}
                onChange={handleCourierNameChange}
              />
 
              {isDropdownVisible && filteredCouriers.length > 0 && (
                <ul className="dropdown-list">
                  {filteredCouriers.map((courier, i) => (
                    <li
                      key={i}
                      onClick={() => handleCourierSelect(courier)}
                      className="dropdown-item cursor-pointer"
                    >
                      {courier}
                    </li>
                  ))}
                </ul>
              )}
              {noResultsMessage && (
                <div className="no-results-message">{noResultsMessage}</div>
              )}
            </div>
          </div>
        </div>
 
        <div ref={captchaRef}>
          <CaptchaInput
            onChange={handleCaptchaChange}
            value={captchaInput}
            onCaptchaTextChange={handleCaptchaTextChange}
          />
        </div>
 
        {backendErrors.length > 0 && (
          <div className="alert alert-danger mt-3">
            {backendErrors.map((err, i) => (
              <div key={i}>{err}</div>
            ))}
          </div>
        )}
 
        <div className="form-footer d-flex justify-content-end">
          <button
            type="reset"
            className="btn btn-secondary me-3 reset-btn"
            onClick={handleReset}
          >
            <i className="fas fa-rotate-left me-1"></i> Reset
          </button>
          <button type="submit" className="btn btn-primary submit-btn">
            <i className="fas fa-arrow-right me-1"></i> Proceed
          </button>
        </div>
      </form>
      )}
    </Container>
  );
};
 
export default CustomForm;
 